import React from "react";
import { Guid } from "guid-typescript";

import "./DropDown.scss";
import { createPortal } from "react-dom";
import Utilities from "../../foundation/Utility";
import Button from "../buttons/Button";

export interface DropDownOption {
    text: string;
    value: any;
    static?: boolean;
    unselectable?: boolean;
    hidden?: boolean;
    notice?: boolean;
    className?: string;
    order?: number;
    ref?: React.RefObject<HTMLLIElement>;
}

interface DropDownProps {
    id?: string;
    icon?: string;
    label?: string;
    value?: any;
    noType?: boolean;
    button?: boolean;
    blank?: boolean;
    fullWidth?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    focusOpen?: boolean;
    keepOpen?: boolean;
    className?: string;
    tooltip?: string;
    tooltipPlace?: string;
    options: DropDownOption[];
    onChange?: (value: any) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

interface DropDownState {
    ref: React.RefObject<HTMLLabelElement>;
    anchor: string;
    open: boolean;
    focused: boolean;
    typeBuffer: string;
    displayValue: string;
    options?: DropDownOption[];
}

class DropDown extends React.Component<DropDownProps, DropDownState> {
    alphaNumeric =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-/\\ ".split(
            "",
        );

    state = {
        ref: React.createRef<HTMLLabelElement>(),
        anchor: `--${Guid.create().toString()}`,
        open: false,
        focused: false,
        typeBuffer: "",
        displayValue: "",
    } as DropDownState;

    static getDerivedStateFromProps(
        props: DropDownProps,
        state: DropDownState,
    ): Partial<DropDownState> {
        const displayValue =
            props.options?.find(item => item.value === props.value)?.text ??
            (props.noType ? null : props.value?.toString()) ??
            "";
        const options = props.options?.map(option => ({
            ...option,
            ref: option.ref ?? React.createRef<HTMLLIElement>(),
        }));
        if (props.blank && !options?.find(option => option.value === null)) {
            options?.unshift({
                text: "",
                value: null,
                ref: React.createRef<HTMLLIElement>(),
            });
        }
        if (displayValue !== state.displayValue) {
            return { displayValue, options };
        }
        return { options };
    }

    focus() {
        this.state.ref.current?.focus();
        console.log("Focused dropdown");
    }

    open() {
        this.setState({ open: true, typeBuffer: "" });
    }

    close(fromBlur = false) {
        if (!fromBlur) this.state.ref.current?.focus();
        this.setState({ open: false });
    }

    toggle() {
        if (this.props.disabled || this.props.readonly) return;
        if (this.state.open) this.close();
        else this.open();
    }

    handleFocus() {
        setTimeout(() => {
            this.setState({ focused: true });
        });
        if (this.props.focusOpen && !this.state.open) this.open();
        this.props.onFocus?.();
    }

    opId: string;
    handleBlur() {
        const opId = Guid.create().toString();
        this.opId = opId;
        this.setState({ focused: false });
        setTimeout(() => {
            if (!this.state.focused && this.opId === opId) {
                this.close(true);
            }
        }, 100);
        this.props.onBlur?.();
    }

    handleClick(event: React.MouseEvent<HTMLInputElement>) {
        if (this.props.disabled || this.props.readonly) return;
        this.toggle();
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        this.props.onKeyDown?.(event);

        if (event.isPropagationStopped()) {
            event.preventDefault();
            return;
        }

        switch (event.key) {
            case "Enter":
            case " ":
            case "Escape":
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowUp":
            case "ArrowDown":
                if (this.state.open) {
                    event.stopPropagation();
                    event.preventDefault();
                    break;
                }
        }
    }

    handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        this.props.onKeyUp?.(event);

        if (event.isPropagationStopped()) {
            event.preventDefault();
            return;
        }

        switch (event.key) {
            case "Enter":
            case " ":
                this.toggle();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Escape":
                if (this.state.typeBuffer.length > 0) {
                    this.setState({ typeBuffer: "" });
                } else {
                    this.close();
                }
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowDown":
                if (this.state.open) {
                    this.state.options
                        .sort(this.optionSort.bind(this))
                        .filter(
                            item =>
                                !item.unselectable &&
                                !item.notice &&
                                !item.hidden,
                        )[0]
                        .ref?.current?.focus();
                    event.stopPropagation();
                    event.preventDefault();
                }
                break;
            default:
                if (this.alphaNumeric.includes(event.key)) {
                    const buffer = this.state.typeBuffer + event.key;
                    const option = this.state.options
                        .filter(
                            item =>
                                !item.unselectable &&
                                !item.notice &&
                                !item.hidden,
                        )
                        .find(item =>
                            item.text
                                .toLowerCase()
                                .startsWith(buffer.toLowerCase()),
                        );

                    if (option) {
                        if (this.state.open) {
                            option.ref?.current?.focus();
                        } else {
                            this.handleOptionClick(null, option);
                        }
                        this.setState({ typeBuffer: buffer });
                    }
                }
                break;
        }
    }

    handleOptionKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Escape":
            case "Enter":
            case " ":
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    handleOptionKeyUp(
        option: DropDownOption,
        event: React.KeyboardEvent<HTMLInputElement>,
    ) {
        switch (event.key) {
            case "Escape":
                this.close();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Enter":
            case " ":
                this.handleOptionClick(null, option);
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowUp":
                this.prevOption(option)?.ref?.current?.focus();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowDown":
                this.nextOption(option)?.ref?.current?.focus();
                event.stopPropagation();
                event.preventDefault();
                break;
            default:
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    prevOption(current: DropDownOption) {
        const index = this.state.options.indexOf(current);
        return this.state.options
            .sort(this.optionSort.bind(this))
            .slice(0, index)
            .filter(item => !item.unselectable && !item.notice && !item.hidden)
            .pop();
    }

    nextOption(current: DropDownOption) {
        const index = this.state.options.indexOf(current);
        return this.state.options
            .sort(this.optionSort.bind(this))
            .slice(index + 1)
            .filter(
                item => !item.unselectable && !item.notice && !item.hidden,
            )[0];
    }

    handleOptionClick(
        event: React.MouseEvent<HTMLLIElement> | null,
        option: DropDownOption,
    ) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.state.ref.current?.focus();

        if (option.unselectable || option.notice) {
            return;
        }

        this.setState({ displayValue: option.text, open: false });
        this.props.onChange?.(option.value);
    }

    optionSort(a: DropDownOption, b: DropDownOption) {
        return Utilities.notNull(a.order) &&
            Utilities.notNull(b.order) &&
            a.order !== b.order
            ? (a.order ?? 0) - (b.order ?? 0)
            : a.text.localeCompare(b.text);
    }

    render(): React.ReactNode {
        const rect = this.state.ref.current?.getBoundingClientRect();
        const width = rect?.width;
        const isTop = (rect ? rect.top / window.innerHeight : 0) > 0.5;
        const maxHeight = Math.min(
            200,
            isTop
                ? rect?.top - 5
                : window.innerHeight - (rect?.bottom ?? 0) - 3,
        );

        let classes = "drop-down";

        if (this.props.noType) classes += " no-type";
        if (this.props.fullWidth) classes += " full";
        if (this.props.disabled) classes += " disabled";
        if (this.props.button) classes += " with-button";
        if (this.props.className) classes += ` ${this.props.className}`;

        return (
            <>
                {
                    <label
                        ref={this.state.ref}
                        className={classes}
                        style={{ anchorName: this.state.anchor }}
                        data-html={true}
                        data-tip={this.props.tooltip}
                        data-place={this.props.tooltipPlace}>
                        {this.props.button ? (
                            <Button
                                icon={this.props.icon}
                                text={this.props.label}
                                onClick={this.handleClick.bind(this)}
                                onFocus={this.handleFocus.bind(this)}
                                onBlur={this.handleBlur.bind(this)}
                            />
                        ) : (
                            <input
                                type="text"
                                readOnly={
                                    this.props.noType || this.props.readonly
                                }
                                disabled={this.props.disabled}
                                value={this.state.displayValue}
                                onFocus={this.handleFocus.bind(this)}
                                onBlur={this.handleBlur.bind(this)}
                                onClick={this.handleClick.bind(this)}
                                onKeyDown={this.handleKeyDown.bind(this)}
                                onKeyUp={this.handleKeyUp.bind(this)}
                                onChange={() => {}}
                            />
                        )}
                        {this.props.label && (
                            <span className="text">{this.props.label}</span>
                        )}
                    </label>
                }
                {(this.state.open || this.props.keepOpen) &&
                    createPortal(
                        <ul
                            className={`drop-down-options${
                                isTop ? " top" : ""
                            }`}
                            style={{
                                minWidth: `${width ?? 0}px`,
                                maxHeight: `${maxHeight ?? 0}px`,
                                positionAnchor: this.state.anchor,
                            }}>
                            {this.state.options
                                .sort(this.optionSort.bind(this))
                                .map((option, index) =>
                                    option.hidden ? null : (
                                        <li
                                            key={index}
                                            ref={option.ref}
                                            tabIndex={
                                                option.unselectable ||
                                                option.notice
                                                    ? -1
                                                    : 0
                                            }
                                            className={`option ${
                                                option.className || ""
                                            }${
                                                option.unselectable
                                                    ? " unselectable"
                                                    : ""
                                            }${option.notice ? " notice" : ""}${
                                                option.static ? " static" : ""
                                            }${
                                                option.value ===
                                                this.props.value
                                                    ? " selected"
                                                    : ""
                                            }`}
                                            onFocus={this.handleFocus.bind(
                                                this,
                                            )}
                                            onBlur={this.handleBlur.bind(this)}
                                            onKeyDown={this.handleOptionKeyDown.bind(
                                                this,
                                            )}
                                            onKeyUp={this.handleOptionKeyUp.bind(
                                                this,
                                                option,
                                            )}
                                            onClick={event =>
                                                this.handleOptionClick(
                                                    event,
                                                    option,
                                                )
                                            }>
                                            {option.text}
                                        </li>
                                    ),
                                )}
                        </ul>,
                        document.querySelector(".page-elements"),
                    )}
            </>
        );
    }
}

export default DropDown;
